/**
 * De draaitafels van Linde: per machine 36 studiorenders, om de 10°, zoals
 * de 3D-viewer op linde-mh.com ze afspeelt. Dat zijn geen 3D-modellen maar
 * foto's van hun eigen CAD-render — en daarmee de beste maatstaf die er is.
 *
 * `scripts/haal-draaibeelden.mjs` zet ze in `referenties-draai/` (niet in
 * git: 33 MB, en het blijft beeld van Linde). De camera hieronder is per
 * machine teruggerekend met `scripts/ijk-draaitafel.mjs`: die zoekt de
 * stand waarin ons silhouet het best over dat van Linde valt, over alle
 * frames tegelijk.
 */

export interface Draaicamera {
  /** Azimut van frame 0, radialen; 0 kijkt vanaf +z (de vorkkant) naar het model. */
  az0: number
  /** +1: elk volgend frame draait de camera 10° linksom (van boven gezien). */
  zin: 1 | -1
  /** Kijkhoek boven de horizon, radialen: het gemiddelde over de omloop. */
  hoogte: number
  /**
   * De kijkhoek golft mee met de draaiing: de camerabaan van Linde ligt
   * scheef. Bij de C-MATIC zie je het meteen — frame 12 tot 23 bijna op
   * ooghoogte, frame 33 tot 3 van ver boven. `golf` is de uitslag (radialen),
   * `golfFase` het azimut waar hij het hoogst kijkt.
   */
  golf: number
  golfFase: number
  /** Verticale beeldhoek, graden. */
  fov: number
  afstand: number
  /** Draaias en kijkpunt, in modelcoördinaten (meters). */
  doel: [number, number, number]
  /** Verschuiving van het beeldmidden, in fracties van de beeldbreedte. */
  schuif: [number, number]
}

export interface Draaitafel {
  /** Model zoals `Matic` het kent. */
  model: 'K-Matic' | 'R-Matic' | 'L-Matic' | 'C-Matic' | 'T-Matic'
  /** Map onder referenties-draai/ en op linde-mh.com. */
  map: string
  bron: string
  /** Welke frame-nummers er zijn (K-MATIC mist er twee). */
  frames: number[]
  camera: Draaicamera
}

const alle36 = Array.from({ length: 36 }, (_, i) => i)

export const DRAAITAFELS: Draaitafel[] = [
  {
    model: 'K-Matic',
    map: 'k-matic',
    bron: 'turntables/11_Automated-Trucks/BR5231_K-Matic',
    frames: alle36.filter((i) => i !== 2 && i !== 34),
    camera: { az0: 0.913, zin: 1, hoogte: 0.3, golf: 0.137, golfFase: 0.223, fov: 25, afstand: 17.03, doel: [0, 3.099, 0.081], schuif: [0, -0.014] },
  },
  {
    model: 'R-Matic',
    map: 'r-matic',
    bron: 'turntables/11_Automated-Trucks/BR1120_R-Matic_V02',
    frames: alle36,
    camera: { az0: 0.868, zin: 1, hoogte: 0.133, golf: 0.055, golfFase: 0.18, fov: 24.79, afstand: 8.75, doel: [0.015, 1.269, 0.141], schuif: [0, 0.024] },
  },
  {
    model: 'L-Matic',
    map: 'l-matic-core',
    bron: 'turntables/11_Automated-Trucks/BR1195_L-MATIC-core',
    frames: alle36,
    camera: { az0: 0.794, zin: 1, hoogte: 0.3, golf: 0.05, golfFase: -0.235, fov: 3.04, afstand: 76.9, doel: [0, 0.9, -0.131], schuif: [0.0049, 0.008] },
  },
  {
    model: 'C-Matic',
    map: 'c-matic',
    bron: 'turntables/11_Automated-Trucks/BR8925_C-MATIC-10_v2',
    frames: alle36,
    camera: { az0: -2.296, zin: 1, hoogte: 0.341, golf: -0.106, golfFase: 0.856, fov: 26.66, afstand: 4.14, doel: [0, 0.15, 0], schuif: [0, 0.015] },
  },
  {
    // De T-MATIC core (22 oktober): zijn draaitafel staat op de pagina
    // /T-MATIC-core/, niet op de oude /T-MATIC/ die in september is nagekeken.
    model: 'T-Matic',
    map: 't-matic',
    bron: 'turntables/11_Automated-Trucks/BR1153_T-MATIC-core',
    frames: alle36,
    // `ijk T-Matic`, 22 oktober: overlap 0,87.
    camera: { az0: 1.024, zin: 1, hoogte: 0.311, golf: 0.147, golfFase: 0.628, fov: 26.74, afstand: 5.934, doel: [0, 0.859, -0.037], schuif: [0, -0.009] },
  },
]

export const vindDraaitafel = (model: string) => DRAAITAFELS.find((d) => d.model === model)

/** Pad van een frame in de dev-server (de map staat in de projectwortel). */
export const framePad = (d: Draaitafel, i: number) =>
  `/referenties-draai/${d.map}/${String(i).padStart(5, '0')}.png`
